import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KIToolsUser, SelectableUserlistOfBot } from '@shared/index';
import { removeFromArray } from '@reflact/tsutil';
import { firstValueFrom } from 'rxjs';
import { LanguageService } from '@shared-angular/services/language.service';
import { RouteShareService } from './route-share.service';
import { SocketAdminService } from '@shared-angular/services/socketadmin.service';
import { ToastrService } from '@shared-angular/services/toastr.service';


@Injectable({
  providedIn: 'root'
})
export class AccesscontrolService {
  public kiToolUserList: KIToolsUser[] = [];
  public selectableUserlistOfBots: SelectableUserlistOfBot[] = [];
  public filterBotByName: string = "";
  private guestIdCounter: number = 1;

  private _searchString: string = "";
  private _filteredUsers: KIToolsUser[] = [];

  public selectedUserIds: string[] = [];
  public allSelected: boolean = false;

  constructor(
    private http: HttpClient,
    private rss: RouteShareService,
    private toastr: ToastrService,
    protected socketAdminService: SocketAdminService,
    private languageService: LanguageService
  ) {

    socketAdminService.onUserListItemUpdate.subscribe((d: KIToolsUser) => {
      if (this.rss.botConfig._id != d.botId) {
        return;
      }
      const user = this.kiToolUserList.find(u => u.guestId == d.guestId);
      if (user == null) {
        this.kiToolUserList.push(d);
        this.filterUpdate();
        return;
      }
      user.accessToken = d.accessToken;
      user.guestId = d.guestId;
      user.metadata = d.metadata;
      user.stats = d.stats;
      user.created = d.created;
      user.updated = d.updated;
      this.filterUpdate();
    });


    socketAdminService.onUserListItemDelete.subscribe((d: { botId: string, userListItemId: string }) => {
      if (this.rss.botConfig._id != d.botId) {
        return;
      }
      removeFromArray(this.kiToolUserList, (((u) => { return u._id == d.userListItemId; })));
      removeFromArray(this.selectedUserIds, (((u) => { return u == d.userListItemId; })));
      this.filterUpdate();
    });
  }



  public async deleteSelected() {
    await Promise.all(this.selectedUserIds.map(user => this.deleteUserInList(user)));
    this.allSelected = false;
    this.selectedUserIds = [];
  }

  public get filteredUsers() {
    return this._filteredUsers;
  }

  public get exportingUsers() {
    return this._filteredUsers.map(u => {
      return {
        guestId: u.guestId,
        metadata: u.metadata,
        created: u.created,
        updated: u.updated,
        chat: window.location.origin + '/' + this.languageService.getLanguage() + "/botaccess?accesstoken=" + u.accessToken,
        audio: window.location.origin + '/' + this.languageService.getLanguage() + "/botaccess?accesstoken=" + u.accessToken + "&target=audio",
        avatar: window.location.origin + '/' + this.languageService.getLanguage() + "/botaccess?accesstoken=" + u.accessToken + "&target=avatar"
      };
    });
  }

  public get exportingStats() {
    return this._filteredUsers.map(u => {
      return {
        guestId: u.guestId,
        chat: u.stats.chat.length,
        audio: u.stats.audio.length,
        avatar: u.stats.avatar.length
      };
    });
  }

  public get searchString(): string {
    return this._searchString;
  }

  public set searchString(str: string) {
    this._searchString = str;
    this.filterUpdate();

  }
  protected filterUpdate() {
    this._filteredUsers = this.kiToolUserList.filter(g => RegExp(this._searchString.toLocaleLowerCase()).exec(g.guestId.toLowerCase()));
    this._filteredUsers.reverse();
  }

  public get filteredselectableUserlistOfBots() {
    return this.selectableUserlistOfBots.filter(uol => uol.botId != this.rss.botConfig._id && uol.botName.toLowerCase().includes(this.filterBotByName.toLowerCase())).filter((_uol, index) => index < 21);
  }

  public getUserUsageCount(guestId: string) {
    return this.selectableUserlistOfBots.filter((ul) => ul.users.find(u => u.guestId == guestId)).length;
  }
  public async loadSelectableUsers(): Promise<void> {
    const result = await firstValueFrom(this.http.get<SelectableUserlistOfBot[]>("/api/toolbox/access/selectableusers/" + this.rss.botConfig._id));
    this.selectableUserlistOfBots = result;
  }


  public async loadUserList(): Promise<void> {
    const result = await this.getUserList(this.rss.botConfig._id);
    this.kiToolUserList = result;
    this.filterUpdate();
  }

  private async getUserList(botId: string): Promise<KIToolsUser[]> {
    const result = await firstValueFrom(this.http.get<KIToolsUser[]>("/api/toolbox/access/userlist/" + botId));
    return result;
  }

  public async addUserToList(guestId: string = "", metadata: string = ""): Promise<KIToolsUser> {

    if (!guestId) {
      do {
        guestId = `Freigabe-${(this.guestIdCounter++).toString()}`;
      } while (this.kiToolUserList.some(user => user.guestId === guestId));
    }

    const userToAdd: Omit<KIToolsUser, 'stats'> = {
      _id: "",
      guestId: guestId,
      accessToken: "",
      metadata: metadata,
      botId: this.rss.botConfig._id,
      created: "",
      updated: ""
    };

    const createdUser = await firstValueFrom(this.http.post<KIToolsUser>("/api/toolbox/access/addusertolist/" + this.rss.botConfig._id, { user: userToAdd }));
    await this.loadSelectableUsers();
    this.filterUpdate();
    this.toastr.success("SAVED");
    return createdUser;
  }

  public async resetUserStats(userId: string) {
    const userIndex = this.kiToolUserList.findIndex(ul => ul._id == userId);
    if (userIndex >= 0) {
      const updatedUser = await firstValueFrom(this.http.put<KIToolsUser>("/api/toolbox/access/resetstats/" + this.rss.botConfig._id + "/" + userId, { user: this.kiToolUserList[userIndex] }));
      this.kiToolUserList[userIndex] = updatedUser;
      this.toastr.success("SAVED");
      return updatedUser;
    }
    throw new Error(`Userid ${userId} not found for update`);
  }

  public async updateUserInList(user: KIToolsUser): Promise<KIToolsUser> {

    const userIndex = this.kiToolUserList.findIndex(ul => ul._id == user._id);
    if (userIndex >= 0) {
      if (this.kiToolUserList.filter(u => u.guestId == this.kiToolUserList[userIndex]?.guestId).length > 1) {
        this.toastr.error("SAVED", $localize`:@@disallowed-duplicate-user:Dieser Nutzer wurde bereits hinzugefügt.`);
        throw new Error(`Userid ${user._id} cannot exists multiple times`);
      }
      const updatedUser = await firstValueFrom(this.http.put<KIToolsUser>("/api/toolbox/access/updateuserinlist/" + this.rss.botConfig._id + "/" + user._id, { user }));
      this.kiToolUserList[userIndex] = updatedUser;
      this.toastr.success("SAVED");
      await this.loadSelectableUsers();

      return updatedUser;
    }
    throw new Error(`Userid ${user._id} not found for update`);
  }

  public async deleteUserInList(userId: string): Promise<void> {
    await firstValueFrom(this.http.delete("/api/toolbox/access/removeuserfromlist/" + this.rss.botConfig._id + "/" + userId));
    this.toastr.success("SAVED");
  }
}
